import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OutsideClickHandler } from '@/ui/outside-click-handler/outside-click-handler';
import { styled } from 'goober';

import { Loader } from '@/components/loader/loader';
import { Header } from '@/ui/typography/widgets';
import { EmptyStateWidget } from '@/ui/widget/empty-state-widget';
import { Widget } from '@/ui/widget/widget';

import { useCanEditCompany } from '../../hooks/use-can-edit-company';
import { EditStamp } from '../edit-stamp';
import { COLUMN_1_WIDTH, WIDGET_ROW_2_HEIGHT } from '../utils/constants';
import { WidgetLoaderContainer } from '../widget-loader-container';

import { CompanyFundingEditMode } from './company-funding-edit-mode';
import { CompanyFundingViewMode } from './company-funding-view-mode';
import { useCompanyFundings } from './use-company-fundings.query';
import { useCrunchbaseEnrichmentCheck } from '../../hooks/use-crunchbase-enrichment-check';
import { useCompany } from '../use-company.query';
import type { CompanyFundingStateType } from './company-funding.types';
import { useEditCompanyFundings } from './use-edit-company-fundings.mutation';

export const CompanyFundingWidget = ({
  companyId,
  editable = true,
  className,
  variant = 'vertical',
  header,
}: {
  companyId: string | null;
  editable?: boolean;
  className?: string;
  variant?: 'horizontal' | 'vertical';
  header?: ReactNode;
}) => {
  const { t } = useTranslation('companies');
  const canEditCompany = useCanEditCompany();
  const [isEditMode, setIsEditMode] = useState(false);
  const { mutateAsync, isPending } = useEditCompanyFundings();

  const [currentFundings, setCurrentFundings] =
    useState<CompanyFundingStateType>({
      capitalRaised: null,
      fundingStageId: null,
      fundingStageName: null,
      lastFundingDate: null,
    });

  const { data: company } = useCompany(companyId);

  const { data: fundings, isLoading, refetch } = useCompanyFundings(companyId);
  const { isEnrichedFromCrunchbase, lastEditedAt } =
    useCrunchbaseEnrichmentCheck('funding');

  useEffect(() => {
    if (fundings) {
      setCurrentFundings({
        capitalRaised: fundings.capitalRaised?.value,
        fundingStageId: fundings.fundingStage?.value?.id ?? null,
        fundingStageName: fundings.fundingStage?.value?.name ?? null,
        lastFundingDate: fundings.lastFundingDate?.value,
      });
    }
  }, [fundings]);

  const handleSaveData = useCallback(async () => {
    const startingData = fundings;
    const payload = {
      ...(startingData?.capitalRaised.value !==
        currentFundings?.capitalRaised && {
        capitalRaised: currentFundings?.capitalRaised,
      }),
      ...(startingData?.fundingStage.value?.id !==
        currentFundings?.fundingStageId && {
        fundingStageId: currentFundings?.fundingStageId,
      }),
      ...(startingData?.lastFundingDate.value !==
        currentFundings?.lastFundingDate && {
        lastFundingDate: currentFundings?.lastFundingDate,
      }),
    };

    if (Object.keys(payload).length > 0) {
      await mutateAsync(payload);
      await refetch();
    }
    setIsEditMode(false);
  }, [currentFundings, fundings]);

  if (isPending || isLoading) {
    return (
      <WidgetLoaderContainer height={WIDGET_ROW_2_HEIGHT} className={className}>
        <Loader />
      </WidgetLoaderContainer>
    );
  }

  const handleEditMode = () => {
    if (canEditCompany) setIsEditMode(true);
  };

  if (
    !isEditMode &&
    editable &&
    !fundings?.capitalRaised.value &&
    !fundings?.fundingStage.value &&
    !fundings?.lastFundingDate.value
  ) {
    return (
      <EmptyStateWidget
        key="empty"
        className={className}
        height={WIDGET_ROW_2_HEIGHT}
        label={
          canEditCompany ? t`funding.addInfoHeader` : t`funding.noInfoHeader`
        }
        labelAlign="center"
        linkText={canEditCompany ? t`funding.addInfoButton` : undefined}
        minWidth={`${COLUMN_1_WIDTH}px`}
        onEditClick={canEditCompany ? handleEditMode : undefined}
      />
    );
  }

  return (
    <OutsideClickHandler onOutsideClick={handleSaveData} disabled={!isEditMode}>
      <Container
        className={className}
        header={header ?? <Header>{t`funding.header`}</Header>}
        height={WIDGET_ROW_2_HEIGHT}
        onEditClick={handleEditMode}
        disabled={!canEditCompany || isPending || !editable}
        isEditMode={isEditMode}
      >
        {!isEditMode && fundings ? (
          <CompanyFundingViewMode data={fundings} variant={variant} />
        ) : (
          fundings && (
            <CompanyFundingEditMode
              fundingData={currentFundings}
              setFundingData={setCurrentFundings}
              disabled={!canEditCompany || isPending || !editable}
            />
          )
        )}
        {!isEditMode && isEnrichedFromCrunchbase && (
          <CrunchbaseBadge
            crunchbasePermalink={company?.crunchbasePermalink ?? null}
            lastEdited={lastEditedAt}
            align="left"
          />
        )}
      </Container>
    </OutsideClickHandler>
  );
};

const Container = styled(Widget)`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const CrunchbaseBadge = styled(EditStamp)`
  flex: 1;
  align-items: end;
`;
